import ExcelJS from 'exceljs';
import { commonVaribles } from './constVariables';

export const patchDataIntoExcel = async (
	data,
	tataLogoBase64,
	templateUrl,
	templateFileName
) => {
	const mappings = data?.mappings;
	const mappingsBelow = data?.mappingsBelow;
	const mappingsSecondRight = data?.mappingsSecondRight;
	const mappingsSplittingSecondary = data?.mappingsSplittingSecondary;
	const mappingsSplittingPrimary = data?.mappingsSplittingPrimary;
	const mappingsSplittingApproval = data?.mappingsSplittingApproval;
	const imageMappings = data?.imageMappings;

	const downloadImageAsBase64 = async (imgUrl) => {
		try {
			const response = await fetch(imgUrl);
			if (!response.ok) {
				throw new Error(`Failed to fetch image: ${response.statusText}`);
			}
			const blob = await response.blob();

			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onloadend = () => resolve(reader.result.split(',')[1]);
				reader.onerror = reject;
				reader.readAsDataURL(blob);
			});
		} catch (error) {
			// Error downloading image from ${imgUrl}
			return null; // Or provide a fallback image URL
		}
	};

	const processImageMappings = async (workbook, worksheet, imageMappings) => {
		const imageWidth = 150; // Set the default width for all images
		const imageHeight = 150; // Set the default height for all images

		for (const { value: imgUrl, position } of imageMappings) {
			if (imgUrl) {
				const imgBase64 = await downloadImageAsBase64(imgUrl);
				if (imgBase64) {
					const imageId = workbook.addImage({
						base64: imgBase64,
						extension: 'jpg', // Adjust as needed (PNG, etc.)
					});

					// Apply the image to the specified position in the worksheet
					worksheet.addImage(imageId, {
						tl: position, // Top-left position (from imageMappings)
						ext: { width: imageWidth, height: imageHeight }, // Set the image size
					});
				} else {
					// Image URL is not valid for ${imgUrl}
				}
			} else {
				// No image found for ${position}
			}
		}
	};

	// Fetch the template Excel file
	const fileResponse = await fetch(templateUrl);
	const blob = await fileResponse.blob();
	const arrayBuffer = await blob.arrayBuffer();

	// Load the workbook from the template
	const workbook = new ExcelJS.Workbook();
	await workbook.xlsx.load(arrayBuffer);

	// Select the first sheet in the template
	const worksheet = workbook.worksheets[0];

	const imageId = workbook.addImage({
		base64: tataLogoBase64,
		extension: 'jpg', // Adjust as needed (PNG, etc.)
	});

	const imageWidth = 140; // Set the default width for all images
	const imageHeight = 120; // Set the default height for all images

	// Apply the image to the specified position in the worksheet
	worksheet.addImage(imageId, {
		tl: 'C3:E7', // Top-left position (from imageMappings)
		ext: { width: imageWidth, height: imageHeight }, // Set the image size
	});
	// Call the function
	await processImageMappings(workbook, worksheet, imageMappings);

	// Map the values to the cells to the right of the matching keys
	mapValuesToCells(worksheet, mappings);

	//Map the values to the cells to below of the matching keys
	mapValuesBelowHeaders(worksheet, mappingsBelow);

	//Map the values to the cells to second right of the matching keys
	mapValuesToSpecificColumn(
		worksheet,
		mappingsSecondRight,
		'2 SECONDARY PACKAGING (Unit Shipping Load)'
	);

	//Map the values to the cells to second right of the matching keys with splitting
	mapValuesToSpecificColumnWithSplitting(
		worksheet,
		mappingsSplittingSecondary,
		'2 SECONDARY PACKAGING (Unit Shipping Load)'
	);

	mapValuesToSpecificColumnWithSplitting(
		worksheet,
		mappingsSplittingPrimary,
		'1 PRIMARY PACKAGING'
	);

	mapValuesToSpecificColumn(worksheet, mappingsSplittingApproval, 'Date');

	// Write the modified workbook to a buffer
	const modifiedBuffer = await workbook.xlsx.writeBuffer();

	// Create a Blob object from the buffer
	const modifiedBlob = new Blob([modifiedBuffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});

	// Create a download link and trigger a download
	const newFileURL = URL.createObjectURL(modifiedBlob);
	const a = document.createElement('a');
	a.href = newFileURL;
	a.download = templateFileName || commonVaribles.sample_excel_template_name;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);

	return true;
};

const mapValuesToCells = (worksheet, mappings) => {
	const normalizeText = (text) => {
		if (typeof text !== 'string') {
			return ''; // Safeguard for non-string values
		}
		return text
			.replace(/[-:.()/]/g, '') // Remove -, :, ., (), and /
			.replace(/\s+/g, ' ') // Normalize spaces (remove extra spaces between words)
			.trim() // Trim leading and trailing spaces
			.toLowerCase(); // Convert to lowercase
	};

	// Check if a cell is part of a merged range
	const isMergedCell = (cell) => {
		return Object.values(worksheet._merges).some(
			(range) => range.model.top === cell.row && range.model.left === cell.col
		);
	};

	// Get the merged range that includes a specific cell
	const getMergedRange = (cell) => {
		return Object.values(worksheet._merges).find(
			(range) =>
				range.model.top <= cell.row &&
				range.model.bottom >= cell.row &&
				range.model.left <= cell.col &&
				range.model.right >= cell.col
		);
	};

	// Starting to map values to cells
	mappings.forEach((mapping) => {
		// Processing mapping: ${mapping.key}
		const normalizedKey = normalizeText(mapping.key); // Normalize the key before comparison
		let matchFound = false; // Flag to stop further iteration once a match is found

		worksheet.eachRow((row, rowNumber) => {
			if (matchFound) return; // Stop processing the row once the match is found
			// Checking row ${rowNumber}

			row.eachCell((cell, colNumber) => {
				const normalizedCellValue = normalizeText(cell.value || ''); // Normalize cell value
				// Checking cell at Row: ${rowNumber}, Column: ${colNumber}, Value: "${cell.value}", Normalized Value: "${normalizedCellValue}"

				// Perform an exact match between the normalized cell value and the normalized key
				if (normalizedCellValue === normalizedKey) {
					//  Found exact match for key: "${mapping.key}" at Row: ${rowNumber}, Column: ${colNumber}`

					// Find the merged range of the matched header (if any)
					let nextCol = colNumber + 1;
					const headerMergedRange = getMergedRange(cell);

					if (headerMergedRange) {
						//  Header is merged from column ${headerMergedRange.model.left} to ${headerMergedRange.model.right}
						nextCol = headerMergedRange.model.right + 1; // Start looking after the rightmost column of the merged range
					}

					let nextCell = worksheet.getCell(rowNumber, nextCol);

					// Check if the next cell is merged or unmerged
					const targetMergedRange = getMergedRange(nextCell);
					if (targetMergedRange) {
						// If the next cell is part of a merged range, update the whole range
						const startCell = worksheet.getCell(
							targetMergedRange.model.top,
							targetMergedRange.model.left
						);
						startCell.value = mapping.value || ''; // Update the first cell in the merged range
					} else {
						// If the next cell is unmerged, update just that cell
						nextCell.value = mapping.value || '';
					}

					// Mark that the match has been found and break out of the loop
					matchFound = true;
					return; // Stop checking cells in the current row
				}
			});
		});
	});
};

const mapValuesBelowHeaders = (worksheet, mappings) => {
	const normalizeText = (text) => {
		if (typeof text !== 'string') {
			return ''; // Safeguard for non-string values
		}
		return text
			.replace(/[-:.()/]/g, '') // Remove -, :, ., (), and /
			.replace(/\s+/g, ' ') // Normalize spaces (remove extra spaces between words)
			.trim() // Trim leading and trailing spaces
			.toLowerCase(); // Convert to lowercase
	};

	// Check if a cell is part of a merged range
	const isMergedCell = (cell) => {
		return Object.values(worksheet._merges).some(
			(range) => range.model.top === cell.row && range.model.left === cell.col
		);
	};

	// Get the merged range that includes a specific cell
	const getMergedRange = (cell) => {
		return Object.values(worksheet._merges).find(
			(range) =>
				range.model.top <= cell.row &&
				range.model.bottom >= cell.row &&
				range.model.left <= cell.col &&
				range.model.right >= cell.col
		);
	};

	// Map values below headers
	mappings.forEach((mapping) => {
		// Processing mapping: ${mapping.key}
		const normalizedKey = normalizeText(mapping.key); // Normalize the key before comparison
		let matchFound = false; // Flag to stop further iteration once a match is found

		worksheet.eachRow((row, rowNumber) => {
			if (matchFound) return; // Stop processing the row once the match is found

			row.eachCell((cell, colNumber) => {
				const normalizedCellValue = normalizeText(cell.value || ''); // Normalize cell value

				// Perform an exact match between the normalized cell value and the normalized key
				if (normalizedCellValue === normalizedKey) {
					// Found exact match for key: "${mapping.key}" at Row: ${rowNumber}, Column: ${colNumber}
					// Find the merged range of the matched header (if any)
					let nextRow = rowNumber + 1;
					const headerMergedRange = getMergedRange(cell);

					if (headerMergedRange) {
						// Header is merged from row ${headerMergedRange.model.top} to ${headerMergedRange.model.bottom}
						nextRow = headerMergedRange.model.bottom + 1; // Start looking below the merged header
					}

					// Ensure only the first column under the header is updated
					const firstColumnInMergedRange = headerMergedRange
						? headerMergedRange.model.left
						: colNumber;

					if (colNumber === firstColumnInMergedRange) {
						let nextCell = worksheet.getCell(nextRow, colNumber);

						// Check if the next cell is merged or unmerged
						const targetMergedRange = getMergedRange(nextCell);
						if (targetMergedRange) {
							// If the next cell is part of a merged range, update the whole range
							const startCell = worksheet.getCell(
								targetMergedRange.model.top,
								targetMergedRange.model.left
							);
							startCell.value = mapping.value || ''; // Update the first cell in the merged range
						} else {
							// If the next cell is unmerged, update just that cell
							nextCell.value = mapping.value || '';
						}

						// Mark that the match has been found and break out of the loop
						matchFound = true;
						return; // Stop checking cells in the current row
					} else {
						// Skipping column ${colNumber} as it's not the first column under the header.`
					}
				}
			});
		});
	});
};

const mapValuesToSpecificColumn = (worksheet, mappings, matchCellValue) => {
	const normalizeText = (text) => {
		if (typeof text !== 'string') {
			return ''; // Safeguard for non-string values
		}
		return text
			.replace(/[-:.()/]/g, '') // Remove -, :, ., (), and /
			.replace(/\s+/g, ' ') // Normalize spaces (remove extra spaces between words)
			.trim() // Trim leading and trailing spaces
			.toLowerCase(); // Convert to lowercase
	};

	// Check if a cell is part of a merged range
	const getMergedRange = (cell) => {
		return Object.values(worksheet._merges).find(
			(range) =>
				range.model.top <= cell.row &&
				range.model.bottom >= cell.row &&
				range.model.left <= cell.col &&
				range.model.right >= cell.col
		);
	};

	// Step 1: Find the column of the cell that matches 'matchCellValue'
	let targetColumn = null;

	worksheet.eachRow((row, rowNumber) => {
		row.eachCell((cell, colNumber) => {
			const normalizedCellValue = normalizeText(cell.value || '');
			if (normalizedCellValue === normalizeText(matchCellValue)) {
				// Found match for cell value: "${matchCellValue}" at Row: ${rowNumber}, Column: ${colNumber}
				const mergedRange = getMergedRange(cell);
				if (mergedRange) {
					// If the cell is part of a merged range, use the leftmost column of the merged range
					targetColumn = mergedRange.model.left;
					// Cell is part of a merged range, using column: ${targetColumn}
				} else {
					// If the cell is not part of a merged range, use the column of the matched cell
					targetColumn = colNumber;
					// Cell is not merged, using column: ${targetColumn}
				}
				return; // Stop looking once we've found the match
			}
		});
	});

	if (targetColumn === null) {
		return; // Exit if no match for the provided cell value is found
	}

	// Step 2: Map the values into the determined column based on the matching headers
	mappings.forEach((mapping) => {
		const normalizedKey = normalizeText(mapping.key); // Normalize the key before comparison
		let matchFound = false; // Flag to stop further iteration once a match is found

		worksheet.eachRow((row, rowNumber) => {
			if (matchFound) return; // Stop processing the row once the match is found
			row.eachCell((cell, colNumber) => {
				const normalizedCellValue = normalizeText(cell.value || ''); // Normalize cell value

				// Perform an exact match between the normalized cell value and the normalized key (header)
				if (normalizedCellValue === normalizedKey) {
					// Found exact match for header: "${mapping.key}" at Row: ${rowNumber}, Column: ${colNumber}

					// Patch the value into the previously found column (targetColumn) in the same row as the header
					let targetCell = worksheet.getCell(rowNumber, targetColumn);

					const targetMergedRange = getMergedRange(targetCell);
					if (targetMergedRange) {
						// If the target cell is part of a merged range, update the whole range
						const startCell = worksheet.getCell(
							targetMergedRange.model.top,
							targetMergedRange.model.left
						);
						// Updating merged cell range: (${targetMergedRange.model.top}, ${targetMergedRange.model.left}) to (${targetMergedRange.model.bottom}, ${targetMergedRange.model.right})
						startCell.value = mapping.value || ''; // Update the first cell in the merged range
					} else {
						// If the target cell is unmerged, update just that cell
						targetCell.value = mapping.value || '';
						// Updated cell at Row: ${rowNumber}, Column: ${targetColumn} with value: "${mapping.value}"
					}

					matchFound = true; // Stop searching for headers once the value is patched
					return;
				}
			});
		});
	});
};

const mapValuesToSpecificColumnWithSplitting = (
	worksheet,
	mappings,
	matchCellValue
) => {
	const normalizeText = (text) => {
		if (typeof text !== 'string') {
			return ''; // Safeguard for non-string values
		}
		return text
			.replace(/[-:.()/]/g, '') // Remove -, :, ., (), and /
			.replace(/\s+/g, ' ') // Normalize spaces (remove extra spaces between words)
			.trim() // Trim leading and trailing spaces
			.toLowerCase(); // Convert to lowercase
	};

	// Check if a cell is part of a merged range
	const getMergedRange = (cell) => {
		return Object.values(worksheet._merges).find(
			(range) =>
				range.model.top <= cell.row &&
				range.model.bottom >= cell.row &&
				range.model.left <= cell.col &&
				range.model.right >= cell.col
		);
	};

	// Step 1: Find the column of the cell that matches 'matchCellValue'
	let targetColumn = null;

	worksheet.eachRow((row, rowNumber) => {
		row.eachCell((cell, colNumber) => {
			const normalizedCellValue = normalizeText(cell.value || '');
			if (normalizedCellValue === normalizeText(matchCellValue)) {
				const mergedRange = getMergedRange(cell);
				if (mergedRange) {
					// If the cell is part of a merged range, use the leftmost column of the merged range
					targetColumn = mergedRange.model.left;
				} else {
					// If the cell is not part of a merged range, use the column of the matched cell
					targetColumn = colNumber;
				}
				return; // Stop looking once we've found the match
			}
		});
	});

	if (targetColumn === null) {
		return; // Exit if no match for the provided cell value is found
	}

	// Step 2: Map the values into the determined column based on the matching headers, applying splitting logic
	mappings.forEach((mapping) => {
		const normalizedKey = normalizeText(mapping.key); // Normalize the key before comparison
		let matchFound = false; // Flag to stop further iteration once a match is found

		worksheet.eachRow((row, rowNumber) => {
			if (matchFound) return; // Stop processing the row once the match is found
			row.eachCell((cell, colNumber) => {
				const normalizedCellValue = normalizeText(cell.value || ''); // Normalize cell value

				// Perform an exact match between the normalized cell value and the normalized key (header)
				if (normalizedCellValue === normalizedKey) {
					// Split the value by ",", "x", "X", or "*" and patch them into consecutive cells
					const valuesToPatch = mapping.value
						.split(/[,xX*]/)
						.map((val) => val.trim());

					// Start patching from the target column found earlier
					let currentCol = targetColumn;

					valuesToPatch.forEach((val, index) => {
						let targetCell = worksheet.getCell(rowNumber, currentCol);
						const targetMergedRange = getMergedRange(targetCell);

						if (targetMergedRange) {
							// If the target cell is part of a merged range, update the first cell in the merged range
							const startCell = worksheet.getCell(
								targetMergedRange.model.top,
								targetMergedRange.model.left
							);
							startCell.value = val || ''; // Update the first cell in the merged range

							// Move to the next column after the merged range for the next value
							currentCol = targetMergedRange.model.right + 1; // Ensure the next value is patched in the next available cell
						} else {
							// If the target cell is unmerged, update just that cell
							targetCell.value = val || '';

							// Move to the next column for the next value
							currentCol++;
						}
					});

					matchFound = true; // Stop searching for headers once the value is patched
					return;
				}
			});
		});
	});
};
